/**
 * Detect if the user is on a desktop device
 * Used to prompt mobile handoff for better camera quality
 */

export interface DeviceInfo {
  isDesktop: boolean;
  isMobile: boolean;
  isTablet: boolean;
  screenWidth: number;
  userAgent: string;
}

/**
 * Detect device type based on user agent and screen size
 */
export function detectDevice(): DeviceInfo {
  const userAgent = navigator.userAgent.toLowerCase();
  const screenWidth = window.innerWidth;
  
  // Mobile patterns
  const mobilePatterns = [
    /android/i,
    /webos/i,
    /iphone/i,
    /ipad/i,
    /ipod/i,
    /blackberry/i,
    /windows phone/i,
  ];
  
  const isMobileUA = mobilePatterns.some(pattern => pattern.test(userAgent));
  
  // Tablet patterns (larger screens but still touch-based)
  const isTabletUA = /ipad|android(?!.*mobile)/i.test(userAgent);
  
  // Screen size heuristics
  // Mobile: < 768px
  // Tablet: 768px - 1024px
  // Desktop: > 1024px
  const isMobileScreen = screenWidth < 768;
  const isTabletScreen = screenWidth >= 768 && screenWidth <= 1024;
  
  // Combine UA and screen size
  const isMobile = isMobileUA || isMobileScreen;
  const isTablet = (isTabletUA || isTabletScreen) && !isMobileScreen;
  const isDesktop = !isMobile && !isTablet;
  
  return {
    isDesktop,
    isMobile,
    isTablet,
    screenWidth,
    userAgent,
  };
}

/**
 * Check if mobile handoff should be recommended
 * Recommend handoff for desktop users or tablets in landscape
 */
export function shouldRecommendMobileHandoff(): boolean {
  const device = detectDevice();
  
  // Always recommend for desktop
  if (device.isDesktop) {
    return true;
  }
  
  // Recommend for tablets in landscape (width > height)
  if (device.isTablet && window.innerWidth > window.innerHeight) {
    return true;
  }
  
  return false;
}

/**
 * Get a user-friendly device description
 */
export function getDeviceDescription(): string {
  const device = detectDevice();
  
  if (device.isMobile) {
    return 'Mobile Phone';
  } else if (device.isTablet) {
    return 'Tablet';
  } else {
    return 'Desktop Computer';
  }
}
